import { CreatingObject, DropInfo, PastingObjects } from "@runtime/components/Component";
import {
    AugmentedDropZone,
    DesignInteractionInfo,
    DraggingObject,
    SelectedObject,
    SelectedSection,
    SelectedSectionData,
    TemplateInfo,
    editState,
    persistentEditState,
    updateDropMode,
} from "./edit-state";
import { distanceToRect, subtractRects } from "./geometry";
import {
    createFromTemplate,
    deselect,
    deselectAll,
    executeRegionInteraction,
    finishPaste,
    notifyHoverObject,
    propagateMouseEvents,
    resendObjectBounds,
    select,
} from "./index";
import {
    BoundsChangeDTO,
    ComponentDropTargetDTO,
    DropZoneUpdateDTO,
    IdentifySelectedObjectDTO,
    MouseEventDTO,
} from "./shared-types";
import * as windowRpc from "./windowRpc";

let lastHoverSelectableElement: Element | null | undefined = null;

export function setLastHoverSelectableElement(val: typeof lastHoverSelectableElement) {
    lastHoverSelectableElement = val;
}

export interface SelectedTemplate {
    type: "template";
    data: TemplateInfo;
}

// These never make it up the RPC channel, so they can afford to contain non-serialisable data
export interface SelectedDesignInteraction {
    type: "designInteraction";
    data: DesignInteractionInfo;
}

export function isEqualSelection(a: IdentifySelectedObjectDTO, b: IdentifySelectedObjectDTO) {
    if (a.type !== b.type) return false;
    if (a.data.name !== b.data.name) return false;
    if (a.type !== "section") return true;
    if (a.data.id === (b.data as SelectedSectionData).id) return true;
}

function isSelected(other: SelectedObject) {
    for (const so of persistentEditState.selection) {
        if (isEqualSelection(so, other)) return true;
    }
    return false;
}

const isSectionComponentAlreadySelected = (sectionSelection: SelectedSection) =>
    !!persistentEditState.selection.find(
        (s) => (s.type === "component" || s.type === "section") && s.data.name === sectionSelection.data.name
    );

const mouseKeys: (keyof MouseEventDTO)[] = [
    "type",
    "altKey",
    "button",
    "buttons",
    "clientX",
    "clientY",
    "ctrlKey",
    "metaKey",
    // "movementX",
    // "movementY",
    // "offsetX",
    // "offsetY",
    "pageX",
    "pageY",
    "screenX",
    "screenY",
    "shiftKey",
    "x",
    "y",
];

function mouseEventData(e: MouseEvent) {
    const rv = {} as any;
    for (const key of mouseKeys) {
        rv[key] = e[key];
    }
    return rv as MouseEventDTO;
}

function findDropZoneInfo(e: MouseEvent, target: Element | null): [ComponentDropTargetDTO, DropInfo] | undefined {
    let dz: null | AugmentedDropZone = null;
    if (editState.nearestDropZone) {
        dz = editState.nearestDropZone;
    } else {
        const dzElement = target?.closest("[anvil-dropzone]") as HTMLElement;
        if (dzElement) {
            dz = editState.dropZones.get(dzElement)!;
        }
    }
    if (dz) {
        const dropInfo =
            dz.freePlacement?.([{ x: e.pageX - dz.originalBounds.left, y: e.pageY - dz.originalBounds.top }])
                .dropInfo || dz.dropInfo;
        if (dropInfo) {
            const dropTarget: ComponentDropTargetDTO = { type: dz.dropTarget.type, name: dz.dropTarget.name };
            return [dropTarget, dropInfo];
        }
    }
}
function dropCreating(e: MouseEvent, target: Element | null, creating: CreatingObject) {
    const [dropTarget, dropInfo] = findDropZoneInfo(e, target) || [null, null];
    if (dropTarget) {
        const createAnother = e.metaKey || e.ctrlKey;
        windowRpc.raiseEvent("createObject", dropTarget, creating, dropInfo, !createAnother);
        windowRpc.raiseEvent("updateDropZones", []);
    }
}

function dropPasting(e: MouseEvent, target: Element | null, pasting: PastingObjects) {
    const [dropTarget, dropInfo] = findDropZoneInfo(e, target) || [null, null];
    if (dropTarget) {
        finishPaste(dropTarget, dropInfo);
    }
}

function getTarget(e: MouseEvent) {
    return document.elementFromPoint(
        e.pageX - (document.scrollingElement?.scrollLeft || 0),
        e.pageY - (document.scrollingElement?.scrollTop || 0)
    );
}

const distanceToDz = (x: number, y: number, dz: AugmentedDropZone) => {
    let dzRects = [dz.element.getBoundingClientRect()];
    for (const occ of dz.occludingElements || []) {
        const occRect = occ.getBoundingClientRect();
        const newRects: DOMRect[] = [];
        for (const r of dzRects) {
            newRects.push(...subtractRects(r, occRect));
        }
        dzRects = newRects;
    }
    let minDist = Number.MAX_VALUE;
    for (const r of dzRects) {
        const dist = distanceToRect(x, y, r);
        if (dist < minDist) {
            minDist = dist;
        }
    }
    return minDist;
};

/** 
 * Trying to fix a firefox bug on mac, where dragging the scrollbar fires pointer events and causes the components to start dragging
 * See #5482
 * We might need to do more, but this seems good enough for now
 */
function isMaybeClickingDocumentScrollBar(target: any) {
    if (target !== document.documentElement) return false;
    return document.documentElement.scrollHeight > document.documentElement.clientHeight;
}

// So that we know whether we're actually dragging from in here, or
// just happen to be dragging over from somewhere else.
let capturedMouseDown = false;
let lastMouseDownSelected = false;

const mouseInterceptor = (e: MouseEvent | WheelEvent) => {
    if (!editState?.editingForm || !persistentEditState) {
        // We haven't finished loading yet (or we failed to load!)
        return;
    }

    const target = getTarget(e);
    let selectableElement =
        target?.closest("[data-anvil-selectable]") || editState.editingForm?.pyComponent.anvil$hooks.domElement!;

    const continueSearch = () => {
        selectableElement =
            selectableElement.parentElement?.closest("[data-anvil-selectable]") ||
            editState.editingForm?.pyComponent.anvil$hooks.domElement!;
        return describeObjectUnderMouse();
    };
    const describeObjectUnderMouse = (): SelectedObject | SelectedTemplate | SelectedDesignInteraction | undefined => {
        if (!selectableElement) {
            return undefined;
        }
        const componentInfo = editState.componentsByDomNode.get(selectableElement);
        if (componentInfo) {
            return { type: "component", data: { name: componentInfo.name } };
        }
        const slotInfo = editState.slotsByDomNode.get(selectableElement);
        if (slotInfo) {
            return { type: "slot", data: slotInfo };
        }
        const sectionInfo = editState.sectionsByDomNode.get(selectableElement);
        if (sectionInfo) {
            return { type: "section", data: sectionInfo };
        }
        const designInteractionInfo = editState.designInteractionsByDomNode.get(selectableElement);
        if (designInteractionInfo) {
            if (!editState.namesByComponent.has(designInteractionInfo.component)) {
                return { type: "designInteraction", data: designInteractionInfo };
            } else {
                return continueSearch();
            }
        }
        const layoutTemplateInfo = editState.templatesByDomNode.get(selectableElement)!;
        return { type: "template", data: layoutTemplateInfo };
    };

    e.stopPropagation();


    if (target !== editState?.inlineEditing && !isMaybeClickingDocumentScrollBar(target)) {
        e.preventDefault();

        switch (e.type) {
            case "pointerdown":
            case "mousedown":
                capturedMouseDown = true;
                lastMouseDownSelected = false;
                windowRpc.raiseEvent("focusDesigner");
                if (persistentEditState.creating) {
                    dropCreating(e, target, persistentEditState.creating);
                } else if (persistentEditState.pasting) {
                    dropPasting(e, target, persistentEditState.pasting);
                } else {
                    // Select the component we clicked on, or clear the selection.
                    const appendToSelection = e.metaKey || e.ctrlKey || e.shiftKey;
                    const selection = describeObjectUnderMouse();
                    if (!target) {
                        // We probably just clicked on a scrollbar. Don't clear the selection in this case.
                    } else {
                        if (selection?.type === "template") {
                            deselectAll();
                            createFromTemplate(selection.data);
                        } else if (selection?.type === "designInteraction") {
                            selection.data.callbacks[e.type]?.(e);
                        } else if (selection) {
                            const wasSelected = isSelected(selection);
                            if (!wasSelected) {
                                if (selection.type === "section") {
                                    // Only allow selecting sections if the component (or another of its sections) is already selected
                                    if (isSectionComponentAlreadySelected(selection)) {
                                        select(selection, appendToSelection);
                                    } else {
                                        select(
                                            { type: "component", data: { name: selection.data.name } },
                                            appendToSelection
                                        );
                                    }
                                } else {
                                    select(selection, appendToSelection);
                                }
                                lastMouseDownSelected = true;
                            } else {
                                // If it's already selected, it might be part of a group we're about to drag. Don't reduce the selection to only this element, or toggle it, until mouseup.
                            }
                            if (selection.type === "component" || selection.type === "section") {
                                const pyComponent = editState.componentsByName.get(selection.data.name)!;
                                let sectionInfo = undefined;
                                if (selection.type === "section") {
                                    sectionInfo = editState.sectionsByDomNode.get(selectableElement)!;
                                }
                                const requiredSensitivity = wasSelected ? 1 : 2;
                                executeRegionInteraction(
                                    selection.data.name,
                                    pyComponent,
                                    e,
                                    requiredSensitivity,
                                    sectionInfo
                                );
                            }
                        } else if (!appendToSelection) {
                            console.log("Deselect", e);
                            deselectAll(); // We clicked outside the top level form!
                        }
                    }
                }
                break;

            case "pointerup":
            case "mouseup":
                if (editState.dragState) {
                    const { grabMouseX, grabMouseY, grabTime } = editState.dragState;
                    const dx = e.pageX - grabMouseX;
                    const dy = e.pageY - grabMouseY;
                    const dt = Date.now() - grabTime;
                    const distanceDragged = Math.sqrt(dx * dx + dy * dy);
                    let dz: null | AugmentedDropZone = null;
                    if (distanceDragged > 4 && dt > 250) {
                        // Must drag more than 4px and for more than a quarter of a second.
                        if (editState.nearestDropZone) {
                            dz = editState.nearestDropZone;
                        } else {
                            const dzElement = target?.closest("[anvil-dropzone]") as HTMLElement;
                            if (dzElement) {
                                dz = editState.dropZones.get(dzElement)!;
                            }
                        }
                    }
                    drop(dz, e);
                } else if (persistentEditState.creating) {
                    !capturedMouseDown && dropCreating(e, target, persistentEditState.creating);
                } else {
                    const appendToSelection = e.metaKey || e.ctrlKey || e.shiftKey;
                    const selection = describeObjectUnderMouse();
                    if (selection && selection.type !== "template" && selection.type !== "designInteraction") {
                        if (isSelected(selection)) {
                            // Multiple objects are selected, and we've just clicked on one of them.
                            if (appendToSelection) {
                                if (!lastMouseDownSelected) {
                                    deselect(selection);
                                }
                            } else if (persistentEditState.selection.length > 1) {
                                // Select only this one
                                select(selection);
                            }
                        }
                    }
                }
                capturedMouseDown = false;
                break;

            case "dblclick":
                // First check whether we double clicked outside the active YAML carrier. If so, that has special handling.
                if (!target?.closest("[anvil-active-carrier-component]")) {
                    // It would be nice to be able to just drop the last element of editState.editPath, but it can be something like ['repeating_panel_1', 'item_template'], so that won't work.
                    // Instead we have to do this mess, which also means we can't navigate "up" from the IDE either. We should use something like ['repeating_panel_1.item_template'], probably.
                    const parentYamlCarrierElement =
                        editState.editingForm.pyComponent.anvil$hooks.domElement!.parentElement!.closest(
                            "[data-anvil-yaml-carrier]"
                        )!;
                    const parentYamlCarrier = editState.yamlCarrierByDomNode.get(parentYamlCarrierElement)!;
                    deselectAll();
                    windowRpc.call(
                        "requestChangeEditingPath",
                        parentYamlCarrier.anvil$designerPath!,
                        parentYamlCarrier.anvil$yamlCarrier!.formName
                    );
                } else if (selectableElement) {
                    const object = describeObjectUnderMouse();
                    switch (object?.type) {
                        case "component": {
                            // We double clicked on a component. The component gets to decide what happens now.
                            const { name, pyComponent } = editState.componentsByDomNode.get(selectableElement)!;
                            console.log("DBL");
                            executeRegionInteraction(name, pyComponent, e, 0);
                            break;
                        }
                        case "section": {
                            const pyComponent = editState.componentsByName.get(object.data.name)!;
                            const sectionInfo = editState.sectionsByDomNode.get(selectableElement)!;
                            executeRegionInteraction(object.data.name, pyComponent, e, 0, sectionInfo);
                            break;
                        }
                        case "designInteraction": {
                            object.data.callbacks[e.type]?.(e);
                            break;
                        }
                    }
                }
                break;

            case "pointermove":
            case "mousemove":
                if (persistentEditState.creating || persistentEditState.pasting || editState.dragState) {
                    // DZ hover effect
                    const effectDistance = 55;
                    const minSize = 15;

                    const visibleDzs: AugmentedDropZone[] = [];
                    let nearestDz: null | AugmentedDropZone = null;
                    let nearestDist = Number.MAX_VALUE;
                    for (const [element, dz] of editState.dropZones.entries()) {
                        const { originalBounds, expandable } = dz;
                        const dist = distanceToDz(
                            e.pageX - (document.scrollingElement?.scrollLeft || 0),
                            e.pageY - (document.scrollingElement?.scrollTop || 0),
                            dz
                        );
                        if (dist < nearestDist) {
                            nearestDist = dist;
                            nearestDz = dz;
                        }

                        if (expandable) {
                            if (dist < effectDistance) {
                                element.style.minWidth = element.style.minHeight = `${
                                    minSize - (dist / effectDistance) * minSize
                                }px`;
                                //visibleDzs.push(dz);
                            } else {
                                element.style.minWidth = element.style.minHeight = "0";
                            }
                        }
                        visibleDzs.push(dz);
                    }
                    // if (nearestDz && visibleDzs.indexOf(nearestDz) === -1) {
                    //     visibleDzs.push(nearestDz);
                    // }
                    // Do all these at the end, so we don't force style-recompute per element.

                    // Recalculate the nearest drop zone, which may be different now that everything has moved.
                    nearestDz = null;
                    nearestDist = Number.MAX_VALUE;
                    for (const dz of visibleDzs) {
                        const dist = distanceToDz(
                            e.pageX - (document.scrollingElement?.scrollLeft || 0),
                            e.pageY - (document.scrollingElement?.scrollTop || 0),
                            dz
                        );
                        if (dist < nearestDist) {
                            nearestDist = dist;
                            nearestDz = dz;
                        } else if (dist === nearestDist && nearestDz?.element.contains(dz.element)) {
                            // console.log("%cWarning: multiple dropzones with the same distance to the cursor", "color: orange;", dz.element);
                            // this can happen in freePlacement mode
                            // e.g. a dropzone inside a container within a gridpanel
                            nearestDist = dist;
                            nearestDz = dz;
                        }
                    }
                    const dropzonesForIDE: DropZoneUpdateDTO[] = [];
                    for (const dz of visibleDzs) {
                        const { element, dropTarget, originalBounds, freePlacement, dropInfo, id, occludingElements } =
                            dz;
                        const bounds = element.getBoundingClientRect();
                        const hovering =
                            distanceToDz(
                                e.pageX - (document.scrollingElement?.scrollLeft || 0),
                                e.pageY - (document.scrollingElement?.scrollTop || 0),
                                dz
                            ) === 0;
                        const isCurrent = element === nearestDz?.element;
                        if (freePlacement) {
                            const dropping = editState.dragState
                                ? editState.dragState.objects.map(
                                      ({
                                          originalState: {
                                              position: { x, y, width, height },
                                          },
                                      }) => ({
                                          x: x + e.pageX - (editState.dragState!.grabMouseX ?? 0) - bounds.x,
                                          y: y + e.pageY - (editState.dragState!.grabMouseY ?? 0) - bounds.y,
                                          width,
                                          height,
                                      })
                                  )
                                : [{ x: e.pageX - bounds.x - 75, y: e.pageY - bounds.y - 5, width: 150, height: 10 }];
                            dropzonesForIDE.push(
                                ...freePlacement(dropping).dropLocations.map(({ x, y, width, height }) => ({
                                    id,
                                    originalBounds,
                                    bounds: new DOMRect(x + bounds.x, y + bounds.y, width, height),
                                    targetBounds: dropTarget.getBounds(),
                                    isCurrent,
                                    occlusions: [],
                                    name: dropTarget.name,
                                }))
                            );
                        } else {
                            // TODO: Why do we not take notice of freePlacement here? We should be able to.
                            dropzonesForIDE.push({
                                id,
                                originalBounds,
                                bounds,
                                targetBounds: dropTarget.getBounds(),
                                isCurrent: element === nearestDz?.element || hovering,
                                occlusions: occludingElements
                                    ? occludingElements.map((el) => el.getBoundingClientRect())
                                    : [],
                                name: dropTarget.name,
                            });
                        }
                    }

                    // for (let i = 0; i < visibleDzs.length; i++) {
                    //     if (dropzonesForIDE[i].isCurrent) {
                    //         console.log("DZ: ", visibleDzs[i], visibleDzs[i].dropInfo);
                    //     }
                    // }

                    editState.nearestDropZone = nearestDz;

                    windowRpc.raiseEvent("updateDropZones", dropzonesForIDE);

                    if (editState.dragState) {
                        drag(e.pageX, e.pageY);
                    }
                } else {
                    // We are not creating a component.
                    if (e.buttons === 1 && selectableElement && capturedMouseDown) {
                        // The element we're over must be selected, because mousedown will have already fired.
                        // Start dragging all selected objects.
                        // const object = describeObjectUnderMouse();
                        // if (object && object.type !== 'template') {
                        // }
                        grab(persistentEditState.selection, e.pageX, e.pageY, "page");
                    } else if (e.buttons === 0) {
                        capturedMouseDown = false;
                        // We're not dragging. Hover over things instead.
                        if (lastHoverSelectableElement !== selectableElement) {
                            const selection = describeObjectUnderMouse();
                            if (selection) {
                                const bounds = selectableElement!.getBoundingClientRect();
                                if (selection.type === "section") {
                                    if (isSectionComponentAlreadySelected(selection)) {
                                        notifyHoverObject({ bounds, type: "section", name: "" });
                                    } else {
                                        notifyHoverObject({ bounds, type: "component", name: selection.data.name });
                                    }
                                } else {
                                    notifyHoverObject({
                                        bounds,
                                        type: selection.type,
                                        name:
                                            selection.type === "component" || selection.type === "slot"
                                                ? selection.data.name
                                                : "",
                                    });
                                }
                            } else {
                                notifyHoverObject(null);
                            }
                            lastHoverSelectableElement = selectableElement;
                        }
                    }
                }

                break;
        }
    }
};

windowRpc.registerFn("mouseEvent", (e: MouseEventDTO) => {
    const evt = new CustomEvent(e.type, { bubbles: true, cancelable: false });
    for (const x of [
        "altKey",
        "ctrlKey",
        "shiftKey",
        "metaKey",
        "button",
        "buttons",
        "x",
        "y",
        "clientX",
        "pageX",
        "clientY",
        "pageY" /*"movementX", "offsetX", "screenX", "movementY", "offsetY", "screenY"*/,
    ] as const) {
        // @ts-ignore
        evt[x] = e[x];
    }
    window.document.dispatchEvent(evt);
});

// Used by file dragging, which starts outside the iframe and needs to be able to finish inside.
function maybePropagateMouseEvent(e: MouseEvent) {
    propagateMouseEvents && windowRpc.raiseEvent("mouseEvent", mouseEventData(e));
}
document.addEventListener("mouseup", maybePropagateMouseEvent, true);
document.addEventListener("mousedown", maybePropagateMouseEvent, true);
document.addEventListener("mousemove", maybePropagateMouseEvent, true);

// Prevent mouse events from reaching components in the designer, so people aren't tempted to listen to them.
function blockEvent(e: MouseEvent) {
    e.stopImmediatePropagation();
    const target = getTarget(e);
    if (target !== editState?.inlineEditing) {
        e.preventDefault();
    }
}
document.addEventListener("mousedown", blockEvent, true);
document.addEventListener("mouseup", blockEvent, true);

// Intercept remaining mouse and pointer events for the designer.
document.addEventListener("click", mouseInterceptor, true);
document.addEventListener("dblclick", mouseInterceptor, true);
// Mouse events don't fire over (some) disabled components, so we listen to pointer events too.
document.addEventListener("pointerdown", mouseInterceptor, true);
document.addEventListener("pointerup", mouseInterceptor, true);
document.addEventListener("pointermove", mouseInterceptor, true);

function cloneElement(target: HTMLElement) {
    const clone = target.cloneNode(true) as HTMLElement;
    // radio buttons selected based on their name attribute
    // so we need to clone them with a different name
    clone.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach((node) => {
        node.name = node.name + "-clone";
    });
    return clone;
}

export function grab(
    objects: IdentifySelectedObjectDTO[],
    mouseX: number,
    mouseY: number,
    startLocation: "page" | "mouse"
) {
    const draggingObjects: DraggingObject[] = [];
    let adjust: { x: number; y: number } | null = null;

    for (const object of objects) {
        let element: HTMLElement;
        switch (object.type) {
            case "component":
                element = editState.componentsByName.get(object.data.name)!.anvil$hooks.domElement!;
                break;
            case "slot":
                element = editState.slotsByName.get(object.data.name)!.element;
                break;
            case "section":
                // TODO how to handle sections here?
                continue;
        }

        const { left, top, width, height } = element.getBoundingClientRect();
        const elementBoundingRect = { left, top, width, height };

        if (!adjust && startLocation === "mouse") {
            adjust = { x: mouseX - elementBoundingRect.left, y: mouseY - elementBoundingRect.top };
        }

        if (adjust) {
            elementBoundingRect.left += adjust.x;
            elementBoundingRect.top += adjust.y;
        }

        const dummyElement = cloneElement(element);
        const bodyBoundingRect = document.body.getBoundingClientRect();

        const position = new DOMRect(
            elementBoundingRect.left - bodyBoundingRect.left,
            elementBoundingRect.top - bodyBoundingRect.top,
            elementBoundingRect.width,
            elementBoundingRect.height
        );

        dummyElement.style.position = "absolute";
        dummyElement.style.top = `${position.top}px`;
        dummyElement.style.left = `${position.left}px`;
        dummyElement.style.width = `${position.width}px`;
        dummyElement.style.height = `${position.height}px`;
        dummyElement.style.opacity = "0.8";
        dummyElement.style.pointerEvents = "none";
        document.body.appendChild(dummyElement);

        draggingObjects.push({
            object,
            element,
            dummyElement,
            originalState: {
                position,
                opacity: element.style.opacity,
            },
        });
        element.style.opacity = "0.2";
    }

    if (!draggingObjects.length) return;

    editState.dragState = {
        objects: draggingObjects,
        grabMouseX: mouseX,
        grabMouseY: mouseY,
        grabTime: Date.now(),
    };

    updateDropMode();
    windowRpc.raiseEvent("setDragging", true);
}

function drag(mouseX: number, mouseY: number) {
    const { objects, grabMouseX, grabMouseY } = editState.dragState!;
    const updatedBounds: BoundsChangeDTO[] = [];

    for (const {
        object: {
            type,
            data: { name },
        },
        dummyElement,
        originalState: { position },
    } of objects) {
        dummyElement.style.left = `${position.left + (mouseX - grabMouseX)}px`;
        dummyElement.style.top = `${position.top + (mouseY - grabMouseY)}px`;

        updatedBounds.push({
            type,
            name,
            bounds: dummyElement.getBoundingClientRect(),
        });
    }

    resendObjectBounds(updatedBounds);
}

export async function drop(dz: AugmentedDropZone | null, e?: MouseEvent) {
    const { objects, grabMouseX, grabMouseY } = editState.dragState!;

    for (const {
        dummyElement,
        element,
        originalState: { opacity },
    } of objects) {
        dummyElement.remove();
        element.style.opacity = opacity;
    }

    editState.dragState = null;
    await updateDropMode();

    if (dz) {
        const dropTarget: ComponentDropTargetDTO = { type: dz.dropTarget.type, name: dz.dropTarget.name };
        const bounds = dz.element.getBoundingClientRect();
        let lastY = 0;
        const dropInfo =
            dz.dropInfo ||
            dz.freePlacement!(
                objects.map(
                    ({
                        originalState: {
                            position: { x, y, width, height },
                        },
                    }) =>
                        e
                            ? {
                                  x: e.pageX - bounds.x + x - (grabMouseX ?? 0),
                                  y: e.pageY - bounds.y + y - (grabMouseY ?? 0),
                                  width,
                                  height,
                              }
                            : { x: 0, y: (lastY += height) - height, width, height }
                )
            ).dropInfo;
        windowRpc.raiseEvent(
            "moveObjects",
            objects.map(
                ({
                    object: {
                        type,
                        data: { name },
                    },
                }) => ({ type, name })
            ),
            dropTarget,
            dropInfo
        );
        windowRpc.raiseEvent("updateDropZones", []);
    } else {
        resendObjectBounds(
            objects.map(
                ({
                    object: {
                        type,
                        data: { name },
                    },
                    element,
                }) => ({ type, name, bounds: element.getBoundingClientRect() })
            )
        );
    }
    windowRpc.raiseEvent("setDragging", false);
}

export async function cancelDrag() {
    if (editState?.dragState) {
        const { objects, grabMouseX, grabMouseY } = editState.dragState!;

        for (const {
            dummyElement,
            element,
            originalState: { opacity },
        } of objects) {
            dummyElement.remove();
            element.style.opacity = opacity;
        }

        editState.dragState = null;
        await updateDropMode();
        resendObjectBounds(
            objects.map(
                ({
                    object: {
                        type,
                        data: { name },
                    },
                    element,
                }) => ({ type, name, bounds: element.getBoundingClientRect() })
            )
        );
        windowRpc.raiseEvent("setDragging", false);
    }
}
